/**
 * Wave 3 · 22 #9 — site requests → LPO draft with management approval (pure functions).
 *
 * A draft moves Draft → Pending approval → Approved → sent (the LPO becomes an open order). Above the approval
 * limit (Admin setting, AED 50,000 by default) management (Admin / Super Admin) must approve it before it can be
 * sent — the server enforces that; these helpers only decide which button the page shows. Every line needs a price
 * before a draft can be submitted or sent (an unpriced line would count as AED 0 against the limit), and a draft that
 * changed after it was submitted — or that was "ready" under a higher limit — goes back for approval.
 */
import { ageDays, toNumber } from '@/lib/format';

const num = (value) => toNumber(value) ?? 0;
const text = (value) => String(value ?? '').trim();
const lower = (value) => text(value).toLowerCase();

export const DRAFT_STATUSES = ['Draft', 'Pending approval', 'Approved'];
export const DEFAULT_APPROVAL_THRESHOLD = 50000;

export const isDraftLpo = (lpo) => DRAFT_STATUSES.some((status) => status.toLowerCase() === lower(lpo?.status));

/** Σ qty × unit price of an LPO's lines. */
export function linesValue(lines) {
  return (Array.isArray(lines) ? lines : []).reduce((sum, line) => sum + num(line.ordered_qty) * num(line.unit_price), 0);
}

/** What the approval limit is checked against: the LPO value or its lines, whichever is higher (as the server). */
export const approvalAmount = (lpo) => Math.max(num(lpo?.total_value), linesValue(lpo?.line_items));

/** How many site requests a draft came from. */
export function draftRequestCount(lpo) {
  if (Array.isArray(lpo?.site_request_ids)) return lpo.site_request_ids.length;
  return new Set((lpo?.line_items || []).flatMap((line) => line.site_request_ids || [])).size;
}

export const STATUS_TONE = { Draft: 'gray', 'Pending approval': 'amber', Approved: 'navy' };

/** Lines without a unit price above 0 (the server refuses to submit or send the draft until they have one). */
export const unpricedLines = (lpo) => (Array.isArray(lpo?.line_items) ? lpo.line_items : []).filter((line) => !(num(line.unit_price) > 0));

const CHANGE_WORDS = { supplier: 'Supplier', project: 'Project', currency: 'Currency', parts: 'Parts', quantities: 'Quantities', value: 'Value' };

/** Why a draft is back in Draft after it was submitted: "Supplier changed after it was submitted — submit it again". */
export function changedText(approval) {
  const changed = Array.isArray(approval?.changed) ? approval.changed : [];
  if (changed.includes('limit')) return 'The approval limit was lowered — submit it for approval';
  const words = changed.map((key) => CHANGE_WORDS[key]).filter(Boolean);
  if (!words.length) return 'Changed after it was submitted — submit it again';
  const list = words.map((word, index) => (index ? word.toLowerCase() : word));
  const joined = list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}` : list[0];
  return `${joined} changed after it was submitted — submit it again`;
}

/**
 * The next step of a draft for this person.
 * → { status, tone, amount, needsApproval, unpriced, primary: { key, label } | null, waiting: text | '', returned: note | '',
 *     changed: text | '', limitLowered }
 * primary keys: price (open the draft to price its lines) · submit · send · approve
 */
export function draftStage(lpo, { threshold = DEFAULT_APPROVAL_THRESHOLD, canApprove = false, canEdit = true, now } = {}) {
  const status = DRAFT_STATUSES.find((value) => value.toLowerCase() === lower(lpo?.status)) || text(lpo?.status);
  const amount = approvalAmount(lpo);
  const needsApproval = amount > num(threshold);
  const approval = lpo?.approval || {};
  const unpriced = unpricedLines(lpo).length;
  // "Ready to send" (no approval needed) was decided under the limit of that day: above today's limit it is submitted.
  const limitLowered = status === 'Approved' && approval.status === 'not_required' && needsApproval;
  let primary = null;
  let waiting = '';
  if (status === 'Draft') {
    if (canEdit) {
      if (unpriced) primary = { key: 'price', label: 'Price lines' };
      else primary = needsApproval ? { key: 'submit', label: 'Submit for approval' } : { key: 'send', label: 'Send to supplier' };
    }
  } else if (status === 'Pending approval') {
    if (canApprove) primary = { key: 'approve', label: 'Approve' };
    const days = approval.submitted_at ? ageDays(approval.submitted_at, { now }) : null;
    waiting = `Waiting for management${days ? ` · ${days} d` : days === 0 ? ' · since today' : ''}`;
  } else if (status === 'Approved' && canEdit) {
    if (unpriced) primary = { key: 'price', label: 'Price lines' };
    else primary = limitLowered ? { key: 'submit', label: 'Submit for approval' } : { key: 'send', label: 'Send to supplier' };
  }
  return {
    status,
    tone: STATUS_TONE[status] || 'gray',
    amount,
    needsApproval,
    unpriced,
    limitLowered,
    primary,
    waiting,
    canReturn: canApprove && (status === 'Pending approval' || status === 'Approved'),
    returned: approval.status === 'returned' && status === 'Draft' ? text(approval.note) : '',
    changed: approval.status === 'changed' && status === 'Draft' ? changedText(approval) : limitLowered ? 'The approval limit was lowered — submit it for approval' : '',
    approvedBy: status === 'Approved' && approval.status === 'approved' ? text(approval.approved_by) : '',
  };
}

/* ------------------------------------------------------------------ site requests on drafts */

/** A request can go on a new draft while it is on no LPO and still has something to deliver. */
export const selectableRequest = (request) => !request?.lpo && num(request?.remaining ?? request?.remaining_qty) > 0;

/** "2 selected · 10 pcs" (pieces only when all picked requests share the unit). */
export function selectionSummary(requests, selectedIds) {
  const picked = (Array.isArray(requests) ? requests : []).filter((request) => selectedIds.has(request.id));
  const units = new Set(picked.map((request) => text(request.unit) || 'pcs'));
  const qty = picked.reduce((sum, request) => sum + num(request.remaining ?? request.remaining_qty), 0);
  return { count: picked.length, qty, unit: units.size === 1 ? [...units][0] : '', ids: picked.map((request) => request.id) };
}

const ORDER_STATE = {
  draft: { label: 'In draft', tone: 'gray' },
  pending_approval: { label: 'Awaiting approval', tone: 'amber' },
  approved: { label: 'Approved', tone: 'navy' },
  ordered: { label: 'Ordered', tone: 'blue' },
};

/** "In draft LPO-2026-004" / "Ordered · LPO-…" for a request that is on an LPO (null otherwise). */
export function orderStateLabel(request) {
  const lpo = request?.lpo;
  if (!lpo) return null;
  const state = ORDER_STATE[request.order_state] || ORDER_STATE.ordered;
  return { ...state, number: text(lpo.lpo_number), text: `${state.label} ${text(lpo.lpo_number)}`.trim() };
}
